/**
 * Carcasa de un diálogo modal propio (sin `window.confirm` ni librerías): velo, `role="dialog"` con
 * `aria-modal`, foco inicial, Tab atrapado dentro, Esc y clic en el velo cancelan.
 *
 * Se dibuja en `document.body` (portal) para quedar encima de la ficha. Los eventos de React igual suben
 * por el árbol de componentes, por eso Tab se detiene aquí; Esc se atiende en captura y con
 * `preventDefault`, así la ficha de detrás (que ignora los Esc ya atendidos) no se cierra.
 *
 * Lo usan `DialogoDescartar` y `DialogoConfirmar`. En el celular es una hoja desde abajo (Dialogo.css).
 */
import { useEffect, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import './Dialogo.css';

const FOCALIZABLES =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

interface Props {
  /** Id del título (el nombre del diálogo). */
  etiquetadoPor: string;
  /** Id del texto que lo describe. */
  descritoPor?: string;
  /** Qué enfocar al abrir; si no viene, el primer control. */
  focoInicial?: RefObject<HTMLElement | null>;
  /** Clase extra de la caja (p. ej. el ancho). */
  className?: string;
  /** `role="alertdialog"` para confirmaciones con consecuencias. */
  alerta?: boolean;
  onCancelar: () => void;
  children: ReactNode;
}

export function Dialogo({
  etiquetadoPor,
  descritoPor,
  focoInicial,
  className,
  alerta = false,
  onCancelar,
  children,
}: Props) {
  const caja = useRef<HTMLDivElement>(null);

  // Siempre la última versión de `onCancelar`, sin volver a registrar el oyente ni mover el foco.
  const cancelarActual = useRef(onCancelar);
  useEffect(() => {
    cancelarActual.current = onCancelar;
  }, [onCancelar]);

  useEffect(() => {
    // Al cerrar, el foco vuelve a quien lo abrió (si sigue en pantalla).
    const quienAbrio =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (focoInicial?.current ?? caja.current?.querySelector<HTMLElement>(FOCALIZABLES))?.focus();
    const alTeclear = (evento: globalThis.KeyboardEvent) => {
      if (evento.key !== 'Escape') return;
      evento.preventDefault();
      evento.stopPropagation();
      cancelarActual.current();
    };
    document.addEventListener('keydown', alTeclear, true);
    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      // Solo si nadie movió el foco a otro lado al cerrar (p. ej. quien abre lo devuelve al select).
      const sinFoco = !document.activeElement || document.activeElement === document.body;
      if (sinFoco && quienAbrio?.isConnected && quienAbrio !== document.body) quienAbrio.focus();
    };
    // `focoInicial` es un `useRef` de quien abre: estable, así esto corre solo al abrir.
  }, [focoInicial]);

  const atrapar = (evento: KeyboardEvent) => {
    if (evento.key !== 'Tab' || !caja.current) return;
    // La ficha de detrás también atrapa el foco: este diálogo manda mientras está abierto.
    evento.stopPropagation();
    const lista = Array.from(caja.current.querySelectorAll<HTMLElement>(FOCALIZABLES));
    const primero = lista[0];
    const ultimo = lista[lista.length - 1];
    if (!primero || !ultimo) return;
    if (evento.shiftKey && document.activeElement === primero) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
      evento.preventDefault();
      primero.focus();
    }
  };

  return createPortal(
    <div
      className="dialogo-fondo"
      onMouseDown={(evento) => evento.target === evento.currentTarget && onCancelar()}
    >
      <div
        ref={caja}
        className={className ? `dialogo ${className}` : 'dialogo'}
        role={alerta ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={etiquetadoPor}
        aria-describedby={descritoPor}
        onKeyDown={atrapar}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
